/**
 * 题型注册表。一种题型一条 descriptor，登记它的判分方式、得分算法和答案文本。
 * 屏幕不认识具体题型，只认识 descriptor 的字段。新增题型在这里加一条，
 * DESCRIPTORS 是 mapped 类型，漏题型直接编译失败。
 * 用 model 与同目录的 grading，被 grading、plan、session 与屏幕读到，不解析题块，不碰调度。
 */

import type { Score } from '../model/result'
import { FULL, ZERO, partial } from '../model/result'
import { average } from './grading'
import type {
	AnswerMap,
	BlankData,
	BlankSpec,
	ChoiceData,
	DataMap,
	FreeRecallData,
	LeafKind,
	MatchData,
	MultiChoiceData,
	Normalizer,
	OrderData,
	Question,
	QuestionKind,
	TrueFalseData,
} from '../model/question'

/** 客观题的结论由分数给出，自评题的结论由用户给出。这条区分只有一个来源。 */
export type Scoring = 'auto' | 'self'

export interface GradeOutcome {
	readonly score: Score
	/** 交卷后展示的答案，题型自己决定怎么把它写成一句话。 */
	readonly correctAnswer: string
	/** true 表示得分只是预检，最终结论由用户给。UI 据此显示四档评分按钮。 */
	readonly selfAssess: boolean
}

export interface LeafDescriptor<K extends QuestionKind> {
	readonly kind: K
	readonly structure: 'leaf'
	readonly scoring: Scoring
	/** 纯函数。data 已在 parsing 时通过 schema 校验，这里不再检查作者写的下标是否合法。 */
	grade(data: DataMap[K], value: AnswerMap[K]): GradeOutcome
	correctAnswer(data: DataMap[K]): string
}

/** 组合题没有自己的 grade，它只声明子题结果怎么合并。合并策略留在题型这一层，不进 UI。 */
export interface GroupDescriptor<K extends QuestionKind> {
	readonly kind: K
	readonly structure: 'group'
	readonly scoring: Scoring
	combine(children: readonly GradeOutcome[]): GradeOutcome
	correctAnswerOfChild(index: number, data: DataMap[K]): string
}

export type TypeDescriptor<K extends QuestionKind> = LeafDescriptor<K> | GroupDescriptor<K>

/** 客观题结论的唯一构造处：selfAssess 恒为 false，自评题自己给出 true 的那个对象。 */
function auto(score: Score, correctAnswer: string): GradeOutcome {
	return { score, correctAnswer, selfAssess: false }
}

/** 答案文本各自一处定义，grade 与 correctAnswer 读同一份，所以讲评显示的永远是被判的那句话。 */
function choiceAnswer(data: ChoiceData): string {
	return data.options[data.answer] as string
}

function multiChoiceAnswer(data: MultiChoiceData): string {
	return data.answers.map((index) => data.options[index] as string).join(' / ')
}

function trueFalseAnswer(data: TrueFalseData): string {
	return data.expects ? '对' : '错'
}

/** 把答案填回原文：空格与 blanks 等长由 parsing 保证，所以一次遍历就能走完。 */
function blankAnswer(data: BlankData): string {
	let position = 0
	return data.text.replace(/_{3}/g, () => {
		const blank = data.blanks[position] as BlankSpec
		position += 1
		return blank.accepts[0] as string
	})
}

function orderAnswer(data: OrderData): string {
	return data.answer.map((index) => data.items[index] as string).join(' → ')
}

function matchAnswer(data: MatchData): string {
	return data.pairs.map(([left, right]) => `${left} → ${right}`).join('；')
}

function freeRecallAnswer(data: FreeRecallData): string {
	return data.reference
}

const choice: LeafDescriptor<'choice'> = {
	kind: 'choice',
	structure: 'leaf',
	scoring: 'auto',
	// 下标全等，对则 FULL，否则 ZERO。
	grade: (data, value) => auto(value === data.answer ? FULL : ZERO, choiceAnswer(data)),
	correctAnswer: choiceAnswer,
}

const multiChoice: LeafDescriptor<'multiChoice'> = {
	kind: 'multiChoice',
	structure: 'leaf',
	scoring: 'auto',
	// 下标集合完全相等才给 1，漏选多选都算 0。全或原是卷面规则。
	grade: (data, value) => {
		const given = new Set(value)
		const exact = given.size === data.answers.length && data.answers.every((index) => given.has(index))
		return auto(exact ? FULL : ZERO, multiChoiceAnswer(data))
	},
	correctAnswer: multiChoiceAnswer,
}

const trueFalse: LeafDescriptor<'trueFalse'> = {
	kind: 'trueFalse',
	structure: 'leaf',
	scoring: 'auto',
	grade: (data, value) => auto(value === data.expects ? FULL : ZERO, trueFalseAnswer(data)),
	correctAnswer: trueFalseAnswer,
}

const blank: LeafDescriptor<'blank'> = {
	kind: 'blank',
	structure: 'leaf',
	scoring: 'auto',
	// 逐空比较：applyNormalizers 之后与 accepts 任一项全等即该空满分，总分 = partial(命中数, 空数)。
	grade: (data, value) => {
		const filled = data.blanks.filter((spec, position) => blankAccepted(spec, value[position] ?? '')).length
		return auto(partial(filled, data.blanks.length), blankAnswer(data))
	},
	correctAnswer: blankAnswer,
}

const order: LeafDescriptor<'order'> = {
	kind: 'order',
	structure: 'leaf',
	scoring: 'auto',
	// 位置正确比例：partial(位置相符的项数, items 数)。
	grade: (data, value) => {
		const inPlace = data.answer.filter((item, position) => value[position] === item).length
		return auto(partial(inPlace, data.answer.length), orderAnswer(data))
	},
	correctAnswer: orderAnswer,
}

const match: LeafDescriptor<'match'> = {
	kind: 'match',
	structure: 'leaf',
	scoring: 'auto',
	// 配对正确比例：左项为键，未选择的右项不计分也不给分。
	grade: (data, value) => {
		const paired = data.pairs.filter(([left, right]) => value[left] === right).length
		return auto(partial(paired, data.pairs.length), matchAnswer(data))
	},
	correctAnswer: matchAnswer,
}

const freeRecall: LeafDescriptor<'freeRecall'> = {
	kind: 'freeRecall',
	structure: 'leaf',
	scoring: 'self',
	// 关键词命中率当预检分，selfAssess 为 true，最终档位由用户点。
	grade: (data, value) => {
		const answer = value.toLowerCase()
		const hit = data.keywords.filter((keyword) => answer.includes(keyword.toLowerCase())).length
		return { score: partial(hit, data.keywords.length), correctAnswer: freeRecallAnswer(data), selfAssess: true }
	},
	correctAnswer: freeRecallAnswer,
}

const reading: GroupDescriptor<'reading'> = {
	kind: 'reading',
	structure: 'group',
	scoring: 'auto',
	// 子题得分平均，correctAnswer 按子题顺序编号拼接。
	// average 住在 grading，因为全项目只允许一处除法；代价是这两个模块互相引用，
	// 而 combine 只在两个模块都初始化完之后才被调到。
	combine: (children) => ({
		score: average(children),
		correctAnswer: children.map((child, index) => `${index + 1}. ${child.correctAnswer}`).join('\n'),
		// 子题里只要有自评题，整题的结论就得等用户点，预检分不能替它收尾。
		selfAssess: children.some((child) => child.selfAssess),
	}),
	correctAnswerOfChild: (index, data) => {
		const child = data.questions[index] as Question<LeafKind>
		// 子题恒为叶子。kind 与 data 同源是 parsing 之后的既成事实，编译器读不出这条相关性，在这里宽化一次。
		const leaf = DESCRIPTORS[child.kind] as LeafDescriptor<LeafKind>
		return leaf.correctAnswer(child.data)
	},
}

export const DESCRIPTORS: { readonly [K in QuestionKind]: TypeDescriptor<K> } = {
	choice,
	multiChoice,
	trueFalse,
	blank,
	order,
	match,
	freeRecall,
	reading,
}

export function descriptorFor<K extends QuestionKind>(kind: K): TypeDescriptor<K> {
	return DESCRIPTORS[kind]
}

/** 该题型能否自动出结论。workspace.submit 用它拒绝「客观题被手工打分」这类混用。 */
export function scoringOf(kind: QuestionKind): Scoring {
	return DESCRIPTORS[kind].scoring
}

export type NormalizerFn = (value: string) => string

/**
 * 全角转半角。U+3000 是全角空格，FF01 到 FF5E 是整套全角 ASCII，偏移 0xfee0。
 * 逐个码位判断而不是写字符区间，因为这两个起点差得不止一个偏移。
 */
function toHalfWidth(value: string): string {
	let result = ''
	for (const char of value) {
		const code = char.codePointAt(0) ?? 0
		if (code === 0x3000) result += ' '
		else if (code >= 0xff01 && code <= 0xff5e) result += String.fromCharCode(code - 0xfee0)
		else result += char
	}
	return result
}

/**
 * 归一化函数的唯一定义处，键就是格式规范里的四个名字。
 * 加一种归一化 = 这里加一条 + question.ts 的 enum 加一项，编译器会逼着两边对齐。
 */
export const NORMALIZERS: { readonly [K in Normalizer]: NormalizerFn } = {
	// String.prototype.trim 的空白集已含全角空格，所以只需要一次调用。
	trim: (value) => value.trim(),
	// 统一小写。
	case: (value) => value.toLowerCase(),
	// 全角字符转半角（含空格与 ASCII 区间）。
	width: toHalfWidth,
	// 去掉中英文标点。中文标点本身就在 Unicode 的 P 类里，先 width 后 punct 时半角 ASCII 标点也走同一条规则。
	punct: (value) => value.replace(/\p{P}/gu, ''),
}

/** 按作者写的顺序依次应用。顺序有意义：先 width 再 punct，全角标点才转得成半角。 */
export function applyNormalizers(value: string, specs: readonly Normalizer[]): string {
	return specs.reduce((current, name) => NORMALIZERS[name](current), value)
}

/** 填空单空判分的唯一入口：归一化后与任一 accepts 全等。accepts 永远是字面量，不含正则。 */
export function blankAccepted(spec: BlankSpec, value: string): boolean {
	const given = applyNormalizers(value, spec.normalize)
	return spec.accepts.some((accepted) => applyNormalizers(accepted, spec.normalize) === given)
}

/** 渲染层需要的元信息，例如排序题需不需要打乱。放这里而不是组件里，避免屏幕自己约定。 */
export interface PresentationHints {
	/** 展示前是否要打乱顺序。打乱种子由卡片键派生，同一题每次看到同样顺序。 */
	readonly shuffle: boolean
	readonly multiAnswer: boolean
}

export const HINTS: { readonly [K in QuestionKind]: PresentationHints } = {
	choice: { shuffle: false, multiAnswer: false },
	multiChoice: { shuffle: false, multiAnswer: true },
	trueFalse: { shuffle: false, multiAnswer: false },
	blank: { shuffle: false, multiAnswer: false },
	order: { shuffle: true, multiAnswer: false },
	match: { shuffle: true, multiAnswer: false },
	freeRecall: { shuffle: false, multiAnswer: false },
	reading: { shuffle: false, multiAnswer: false },
}
